'use client';

import type { Area } from 'react-easy-crop';

/**
 * Crops image to a blob using pixel crop from react-easy-crop.
 * Optional rotation matches Cropper `rotation` degrees.
 */
export async function getCroppedImageBlob(params: {
  imageSrc: string;
  pixelCrop: Area;
  rotation?: number;
  mimeType?: string;
  quality?: number;
}): Promise<Blob> {
  const image = await loadImage(params.imageSrc);
  const rotation = params.rotation ?? 0;
  const mimeType = params.mimeType ?? 'image/webp';
  const quality = params.quality ?? 0.9;

  if (!rotation) {
    return cropWithoutRotation(image, params.pixelCrop, mimeType, quality);
  }

  return cropWithRotation(image, params.pixelCrop, rotation, mimeType, quality);
}

async function cropWithoutRotation(
  image: HTMLImageElement,
  pixelCrop: Area,
  mimeType: string,
  quality: number,
): Promise<Blob> {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Canvas 2D context is unavailable');
  }
  canvas.width = Math.max(1, Math.round(pixelCrop.width));
  canvas.height = Math.max(1, Math.round(pixelCrop.height));
  context.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    canvas.width,
    canvas.height,
  );
  return canvasToBlob(canvas, mimeType, quality);
}

async function cropWithRotation(
  image: HTMLImageElement,
  pixelCrop: Area,
  rotation: number,
  mimeType: string,
  quality: number,
): Promise<Blob> {
  const rotRad = (rotation * Math.PI) / 180;
  const { width: bBoxWidth, height: bBoxHeight } = getRotatedBounds(
    image.width,
    image.height,
    rotation,
  );

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Canvas 2D context is unavailable');
  }

  canvas.width = Math.max(1, Math.round(bBoxWidth));
  canvas.height = Math.max(1, Math.round(bBoxHeight));
  context.translate(canvas.width / 2, canvas.height / 2);
  context.rotate(rotRad);
  context.translate(-image.width / 2, -image.height / 2);
  context.drawImage(image, 0, 0);

  const croppedCanvas = document.createElement('canvas');
  const croppedContext = croppedCanvas.getContext('2d');
  if (!croppedContext) {
    throw new Error('Canvas 2D context is unavailable');
  }
  croppedCanvas.width = Math.max(1, Math.round(pixelCrop.width));
  croppedCanvas.height = Math.max(1, Math.round(pixelCrop.height));
  croppedContext.drawImage(
    canvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    croppedCanvas.width,
    croppedCanvas.height,
  );

  return canvasToBlob(croppedCanvas, mimeType, quality);
}

function getRotatedBounds(
  width: number,
  height: number,
  rotation: number,
): {
  width: number;
  height: number;
} {
  const rotRad = (rotation * Math.PI) / 180;
  return {
    width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

function canvasToBlob(canvas: HTMLCanvasElement, mimeType: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (result) => {
        if (!result) {
          reject(new Error('Failed to crop image'));
          return;
        }
        resolve(result);
      },
      mimeType,
      quality,
    );
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', () => reject(new Error('Failed to load image for crop')));
    if (!src.startsWith('blob:')) {
      image.crossOrigin = 'anonymous';
    }
    image.src = src;
  });
}
